import type { RequestCookie } from 'next/dist/compiled/@edge-runtime/cookies';

import type { Person } from '../../payload/payload-types';
import type { PagesQuery } from '../_graphql/__generated__/operations';
import { PAGES } from '../_graphql/pages';
import { TEAM } from '../_graphql/team';
import { GRAPHQL_API_URL } from './shared';
import { payloadToken } from './token';

type PagesDocItem = NonNullable<NonNullable<NonNullable<PagesQuery['Pages']>['docs']>[number]>;

export type CollectionDocsMap = {
  pages: PagesDocItem;
  persons: Person;
};

const queryMap = {
  pages: {
    query: PAGES,
    key: 'Pages',
  },
  persons: {
    query: TEAM,
    key: 'Persons',
  },
};

export const fetchDocs = async <K extends keyof CollectionDocsMap>(
  collection: K,
  draft?: boolean,
  variables?: Record<string, unknown>,
  locale?: string,
): Promise<CollectionDocsMap[K][]> => {
  if (!queryMap[collection]) throw new Error(`Collection ${collection} not found`);

  // Guard against undefined GRAPHQL_API_URL (e.g., during Docker builds without API access)
  if (!GRAPHQL_API_URL) {
    console.warn('[fetchDocs] GRAPHQL_API_URL is not defined, returning empty array');
    return [];
  }

  let token: RequestCookie | undefined;

  if (draft) {
    const { cookies } = await import('next/headers');
    token = (await cookies()).get(payloadToken);
  }

  const docs: CollectionDocsMap[K][] = await fetch(`${GRAPHQL_API_URL}/api/graphql/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token?.value && draft ? { Authorization: `JWT ${token.value}` } : {}),
    },
    next: {
      revalidate: draft ? 0 : 14400, // 4 hours (matches sitemap revalidation)
      tags: [collection, `${collection}_list`]
    },
    body: JSON.stringify({
      query: queryMap[collection].query,
      variables: {
        ...variables,
        draft,
        locale,
      },
    }),
  })
    ?.then(res => res.json())
    ?.then(res => {
      if (res.errors) throw new Error(res?.errors?.[0]?.message ?? 'Error fetching docs');

      return res?.data?.[queryMap[collection].key]?.docs;
    });

  if (process.env.LOG_GRAPHQL_QUERIES === 'true' || process.env.LOG_GRAPHQL_QUERIES === '1') {
    console.log({ docs });
  }
  return docs;
};

export const fetchTeam = async (locale?: string): Promise<Person[]> => {
  return fetchDocs('persons', false, undefined, locale);
};
